import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  Toast,
  ToastTitle,
  ToastDescription,
  useToast,
} from "../gluestack/toast";
import parseRtkError from "ui/utils/Errors";
import { ToastModel } from "ui/types/ToastModel";
import { CloseIcon, Icon, InfoIcon } from "ui/gluestack/icon";
import { AtomsView } from "ui";
import { Pressable } from "react-native";
import { useSelector } from "react-redux";
import { selectIsOnline } from "app/store/slices/network";

export function useDisplayToast() {
  const isOnline = useSelector(selectIsOnline);
  const toast = useToast();

  const renderToast = (uniqueId: string, toastContent: ToastModel) => {
    const toastBg =
      toastContent.type === "error"
        ? "bg-error-800"
        : toastContent.type === "success"
          ? "bg-success-800"
          : "bg-primary-800";

    const onClose = () => {
      toast.close(uniqueId);
    };

    const handleToastPress = () => {
      if (toastContent.onPress) {
        toastContent.onPress();
        toast.close(uniqueId);
      }
    };

    const ToastContent = (
      <AtomsView className="flex flex-row gap-x-3">
        <Icon as={InfoIcon} size="sm" className="stroke-white" />
        <AtomsView className="flex flex-column flex-1">
          <ToastTitle className="font-semibold text-base">
            {toastContent.title}
          </ToastTitle>
          <ToastDescription className="text-sm">
            {toastContent.message}
          </ToastDescription>
        </AtomsView>
        <Pressable onPress={onClose}>
          <Icon as={CloseIcon} size="sm" className="stroke-white" />
        </Pressable>
      </AtomsView>
    );

    return (
      <Toast
        nativeID={uniqueId}
        action={toastContent.type}
        variant="solid"
        className={`${toastBg} max-w-[350px] ${toastContent.onPress ? "cursor-pointer" : ""}`}
      >
        {toastContent.onPress ? (
          <Pressable onPress={handleToastPress} className="flex-1">
            {ToastContent}
          </Pressable>
        ) : (
          ToastContent
        )}
      </Toast>
    );
  };

  const showNewToast = (toastContent: ToastModel) => {
    toast.show({
      id: toastContent?.id ?? undefined,
      placement: "bottom right",
      duration: toastContent?.duration ?? 3000,
      render: ({ id }) => renderToast(id, toastContent),
    });
  };

  const showErrorToast = (
    error: FetchBaseQueryError | SerializedError | string | undefined, // Allow string errors too
    options?: { title?: string; statusOverride?: Record<number, string> },
  ) => {
    if (!error) return;

    const message =
      typeof error === "string"
        ? error
        : parseRtkError(error, options?.statusOverride);

    toast.show({
      placement: "bottom right",
      duration: 5000,
      render: ({ id }) =>
        renderToast(id, {
          title: options?.title || "Something went wrong",
          message,
          type: "error",
        }),
    });
  };

  const showOfflineToast = (
    {
      title = "Request saved offline",
      message = "There was an issue with your internet connection while submitting the request. We'll attempt to resubmit the submissions when back online.",
    }: {
      title?: string;
      message?: string;
    },
    errorCallback?: () => void,
  ) => {
    if (!isOnline) {
      showNewToast({
        title,
        message,
        type: "warning",
      });
    } else {
      errorCallback?.();
    }
  };

  return { showNewToast, showErrorToast, showOfflineToast };
}
